<template>
  <lf-modal
    :title="$t('DEALS.EQUIPMENT_RENTAL.EDIT_INSURANCE_INFO')"
    :close="close"
    :title-border="false"
  >
    <template #content>
      <form novalidate @submit="onSubmit">
        <div class="p-6">
          <div class="flex flex-row flex-wrap space-x-0 sm:space-x-5">
            <lf-input
              v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_company)"
              :placeholder="$t('DEALS.EQUIPMENT_RENTAL.INSURANCE_INFO_COMPANY')"
              :value="fieldData[INSURANCE_FIELD_IDS.insurance_company]"
              :name="INSURANCE_FIELD_IDS.insurance_company"
              type="text"
            />
          </div>
          <div class="flex flex-row flex-wrap space-x-0 sm:space-x-5">
            <lf-input
              v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_first_name)"
              :placeholder="$t('COMMON.FIRST_NAME')"
              :value="fieldData[INSURANCE_FIELD_IDS.insurance_first_name]"
              :name="INSURANCE_FIELD_IDS.insurance_first_name"
            />
            <lf-input
              v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_last_name)"
              :placeholder="$t('COMMON.LAST_NAME')"
              :value="fieldData[INSURANCE_FIELD_IDS.insurance_last_name]"
              :name="INSURANCE_FIELD_IDS.insurance_last_name"
            />
          </div>
          <div class="flex flex-row flex-wrap space-x-0 sm:space-x-5">
            <lf-input
              v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_telephone)"
              :placeholder="$t('COMMON.PHONE')"
              :value="fieldData[INSURANCE_FIELD_IDS.insurance_telephone]"
              :name="INSURANCE_FIELD_IDS.insurance_telephone"
              type="text"
            />
            <lf-input
              v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_email_address)"
              :placeholder="$t('COMMON.EMAIL')"
              :value="fieldData[INSURANCE_FIELD_IDS.insurance_email_address]"
              :name="INSURANCE_FIELD_IDS.insurance_email_address"
              type="text"
            />
          </div>
          <div v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_address)">
            <div class="space-x-0 sm:space-x-5">
              <address-typeahead
                :placeholder="
                  $t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADDRESS_LINE')
                "
                :model-value="address.address_line || ''"
                name="address_line"
                @select="selectAddressSuggestion"
              />
            </div>
            <div class="flex flex-row flex-wrap space-x-0 sm:space-x-5">
              <lf-input
                :placeholder="
                  $t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADDRESS_LINE', {
                    no: 2,
                  })
                "
                :value="address.address_line2"
                name="address_line2"
                type="text"
              />
            </div>
            <div class="flex flex-row flex-wrap space-x-0 sm:space-x-5">
              <lf-input
                :placeholder="$t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.CITY')"
                :value="address.city"
                name="city"
                type="text"
              />
              <lf-input
                :placeholder="$t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.STATE')"
                :value="address.state"
                name="state"
                type="text"
              />
            </div>
            <div class="flex flex-row flex-wrap space-x-0 sm:space-x-5">
              <lf-input
                :placeholder="$t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.ZIP')"
                :value="address.zip"
                name="zip"
                type="text"
              />
              <lf-input
                :placeholder="$t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.COUNTRY')"
                :value="address.country"
                name="country"
                type="text"
              />
            </div>
          </div>
          <div class="mb-6 flex flex-row flex-wrap space-x-0 sm:space-x-5">
            <lf-dropdown
              v-if="
                isFieldVisible(
                  INSURANCE_FIELD_IDS.insurance_has_rental_protection
                )
              "
              :placeholder="
                $t('DEALS.EQUIPMENT_RENTAL.INSURANCE_INFO_PROTECTION_PLAN')
              "
              :value="hasInsurance"
              :name="INSURANCE_FIELD_IDS.insurance_has_rental_protection"
              :options="HAS_INSURANCE_OPTIONS"
              full-width
              class="h-11-5"
            />
            <lf-dropdown
              v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_type)"
              :placeholder="$t('DEALS.EQUIPMENT_RENTAL.INSURANCE_TYPE')"
              :value="fieldData[INSURANCE_FIELD_IDS.insurance_type]"
              :name="INSURANCE_FIELD_IDS.insurance_type"
              :options="insuranceTypes"
              sort
            />
          </div>
          <template
            v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_certificate)"
          >
            <lf-h3 class="mb-6">
              {{ $t("DEALS.EQUIPMENT_RENTAL.CERTIFICATE_OF_INSURANCE") }}
            </lf-h3>
            <button
              v-if="!isDragAndDropOpen"
              type="button"
              class="flex cursor-pointer items-center space-x-3-4 pb-6 font-medium text-primary"
              @click.stop="isDragAndDropOpen = true"
            >
              <icon-base icon="plus-circle" icon-color="#3769D6" />
              <span>{{ $t("DEALS.EQUIPMENT_RENTAL.ADD_NEW_INSURANCE") }}</span>
            </button>
            <div
              v-else
              class="group relative my-6 flex h-80 max-w-full flex-col items-center justify-center border border-dashed border-gray-300 bg-gray-50"
              @drop.prevent="handleDrop"
              @dragenter.prevent
              @dragover.prevent
            >
              <button
                class="absolute top-0 right-4 flex h-6 w-6 -translate-y-1/2 transform items-center justify-center rounded-full border bg-gray-100 text-gray-500 opacity-0 group-hover:opacity-100"
                @click.prevent="isDragAndDropOpen = false"
              >
                <icon-base icon="x" />
              </button>
              <div
                class="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100"
              >
                <icon-base width="30" height="30" icon="upload-file" />
              </div>
              <span class="mt-6 w-60 text-center text-sm text-gray-500">
                {{ $t("DEALS.DOCUMENTS.UPLOAD_TEXT_1") }}
                <input
                  id="browse"
                  type="file"
                  multiple
                  class="hidden"
                  accept=".doc, .pdf, .xls, .xlsx, image/*, .docx"
                  @change="handleFileChange"
                />
                <label for="browse" class="cursor-pointer text-primary">
                  {{ $t("DEALS.DOCUMENTS.UPLOAD_TEXT_BROWSE") }}
                </label>
                {{ $t("DEALS.DOCUMENTS.UPLOAD_TEXT_2") }}
              </span>
            </div>
            <file-list :files="files" delete-btn />
          </template>
        </div>
        <div class="flex justify-end border-t border-gray-200 p-6">
          <outline-button class="mr-3" @click="close">
            {{ $t("COMMON.CANCEL") }}
          </outline-button>
          <color-button
            :disabled="isSubmitting"
            type="submit"
            @click.prevent="onSubmit"
          >
            {{ $t("COMMON.SAVE") }}
          </color-button>
        </div>
      </form>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { useForm } from "vee-validate";

import { ALLOWED_FILES_TYPES, IMAGE_TYPES } from "@/helpers/constants";
import {
  INSURANCE_FIELD_IDS,
  WORKFLOW_BLOCKS_IDS,
} from "@/helpers/constants/workflow";
import { areFilesExtensionsValid, getExtensionFromName } from "@/helpers/files";
import { dispatchAction } from "@/helpers/vee-validate";
import { useDeals } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useWorkflowFields } from "@/hooks/workflow";
import applicationsApiService from "@/services/modules/applications";

import FileList from "./FileList.vue";
import LfModal from "@/components/ui/Modal.vue";
import AddressTypeahead from "@/components/ui/inputs/AddressTypeahead.vue";

import type {
  FileWBlob,
  IAddress,
  IErrorResponse,
  IFile,
} from "@/models/common";
import type { PropType } from "vue";

const props = defineProps({
  files: {
    type: Array as PropType<IFile[]>,
    required: true,
  },
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const HAS_INSURANCE_OPTIONS = { 1: "Yes", 0: "No" };
const hasInsurance = ref(0);

const { commit, getters } = useStore();
const { t } = useI18n();
const { showMessage } = useNotification();
const { activeDeal } = useDeals();
const { isFieldVisible, fieldData } = useWorkflowFields(
  WORKFLOW_BLOCKS_IDS.insurance
);
const isDragAndDropOpen = ref(false);
const filesQueue = ref<FileWBlob[]>([]);
const uploadedFiles = ref<IFile[]>([]);
const address = ref<Partial<IAddress>>({
  address_line: "",
  address_line2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
});

watch(filesQueue, async (value: FileWBlob[]) => {
  if (value.length) {
    await Promise.all(filesQueue.value.map(uploadDocument));
    commit("applications/addFilesToActiveApp", uploadedFiles.value);
    filesQueue.value = [];
    uploadedFiles.value = [];
  }
});

const insuranceTypes = computed<Record<string, string>>(
  () => getters["options/insuranceTypes"]
);

const handleDrop = (dropEvent: DragEvent) => {
  if (dropEvent?.dataTransfer) {
    const fileArray = Object.values(dropEvent.dataTransfer.files);
    if (areFilesExtensionsValid(fileArray, ALLOWED_FILES_TYPES)) {
      enqueueFiles(dropEvent.dataTransfer.files);
    } else {
      showMessage(t("DEALS.EQUIPMENT_RENTAL.INVALID_FILE_TYPE"), "error");
    }
  }
};

const uploadDocument = async (file: FileWBlob) => {
  const data = {
    file_type: "insurance_certificate",
    file: file.blob,
  };

  try {
    showMessage(t("CUSTOMIZE.UPLOADING_1"), "success");
    const response = await applicationsApiService.uploadApplicationFile(
      activeDeal.value.id,
      data
    );
    uploadedFiles.value.push(response);
    showMessage(t("DEALS.EQUIPMENT_RENTAL.FILES_UPLOADED"), "success");
    isDragAndDropOpen.value = false;
    return response;
  } catch (error) {
    showMessage(
      (error as IErrorResponse).response.data.errors.file[0],
      "error"
    );
    return error;
  }
};

const handleFileChange = (e: Event) => {
  enqueueFiles((e?.target as HTMLInputElement)?.files);
  (e.target as HTMLInputElement).value = "";
};

const enqueueFiles = (files: FileList | null) => {
  if (!files) {
    return;
  }
  const toUpload = getFilesWithValidSize(files);
  filesQueue.value = toUpload.map((file) => {
    const type = getExtensionFromName(file.name);
    return {
      name: file.name,
      progress: 0,
      failed: false,
      uploaded: false,
      size: Math.round(file.size / 1024),
      type,
      documentType: null,
      imagePreviewUrl: IMAGE_TYPES.includes(type)
        ? URL.createObjectURL(file)
        : null,
      blob: file,
    };
  });
};

const getFilesWithValidSize = (files: FileList) => {
  const filesArray = Object.values(files);
  const filesWithValidSize = filesArray.filter(
    (file) => Math.round(file.size / 1024) < 100_000
  );
  if (filesArray.length !== filesWithValidSize.length) {
    showMessage(
      t("DEALS.DOCUMENTS.FILE_BIG"),
      "error",
      t("DEALS.DOCUMENTS.FILE_BIG_SUBTITLE")
    );
  }
  return filesWithValidSize;
};

const { handleSubmit, isSubmitting } = useForm();

const onSubmit = handleSubmit(async (values, actions) => {
  if (values.insurance_has_rental_protection === null) {
    delete values.insurance_has_rental_protection;
  }

  address.value.address_line = values.address_line;
  address.value.address_line2 = values.address_line2;
  address.value.city = values.city;
  address.value.state = values.state;
  address.value.zip = values.zip;
  address.value.country = values.country;

  const payload = {
    [INSURANCE_FIELD_IDS.insurance_address]: {},
    [INSURANCE_FIELD_IDS.insurance_company]: values.insurance_company,
    [INSURANCE_FIELD_IDS.insurance_email_address]:
      values.insurance_email_address,
    [INSURANCE_FIELD_IDS.insurance_first_name]: values.insurance_first_name,
    [INSURANCE_FIELD_IDS.insurance_has_rental_protection]:
      values.insurance_has_rental_protection,
    [INSURANCE_FIELD_IDS.insurance_last_name]: values.insurance_last_name,
    [INSURANCE_FIELD_IDS.insurance_telephone]: values.insurance_telephone,
    [INSURANCE_FIELD_IDS.insurance_type]: values.insurance_type,
  };
  if (address.value) {
    payload[INSURANCE_FIELD_IDS.insurance_address] = address.value;
  }

  try {
    await dispatchAction(
      {
        [WORKFLOW_BLOCKS_IDS.insurance]: payload,
      },
      actions,
      "workflows/updateWorkflowSnapshotFields"
    );
    props.close();
  } catch (e) {
    //
  }
});

const selectAddressSuggestion = (addressSuggestion: Partial<IAddress>) => {
  address.value = {
    ...address.value,
    ...addressSuggestion,
  };
};

onMounted(() => {
  if (fieldData.value[INSURANCE_FIELD_IDS.insurance_address]) {
    address.value = fieldData.value[
      INSURANCE_FIELD_IDS.insurance_address
    ] as IAddress;
  }
  hasInsurance.value = fieldData.value[
    INSURANCE_FIELD_IDS.insurance_has_rental_protection
  ]
    ? 1
    : 0;
});
</script>
